// components/Topbar.jsx
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { NAV_ITEMS } from "./navItems";
import { getAvatarUrlFromUser, getDisplayName } from "../utils/avatarUtils";
import { useTranslation } from "react-i18next";
import { useNotifications } from "../context/NotificationContext";
import { getNotifications, markNotificationAsRead, markAllNotificationsAsRead } from "../services/notificationsService";
import { useAuth } from "../context/AuthContext";
import { getTimeAgo } from "../utils/formatters";

export default function Topbar({ active, onSelect, user }) {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { isAuthenticated } = useAuth();
    const { unreadCount, decrementCount, resetCount } = useNotifications();

    const [popoverOpen, setPopoverOpen] = useState(false);
    const [previewNotifications, setPreviewNotifications] = useState([]);
    const [loading, setLoading] = useState(false);

    const popoverRef = useRef(null);

    const homeItem = NAV_ITEMS.find((item) => item.key === "dashboard");
    const navItems = NAV_ITEMS.filter((item) => item.key !== "dashboard" && item.key !== "actions" && item.key !== "notifications");

    const avatarUrl = getAvatarUrlFromUser(user);
    const displayName = getDisplayName(user);

    const getLabel = (item) => t(`common.${item.key}`, { defaultValue: item.label });

    // Close on click outside and escape
    useEffect(() => {
        if (!popoverOpen) return;
        const handleClickOutside = (event) => {
            if (popoverRef.current && !popoverRef.current.contains(event.target)) {
                setPopoverOpen(false);
            }
        };
        const handleEscape = (event) => {
            if (event.key === "Escape") {
                setPopoverOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        document.addEventListener("keydown", handleEscape);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("keydown", handleEscape);
        };
    }, [popoverOpen]);

    const togglePopover = async () => {
        if (!popoverOpen) {
            setPopoverOpen(true);
            setLoading(true);
            try {
                const res = await getNotifications({ page: 1 });
                setPreviewNotifications(res?.items?.slice(0, 5) || []);
            } catch (e) {
                console.error("Error loading preview notifications:", e);
            } finally {
                setLoading(false);
            }
        } else {
            setPopoverOpen(false);
        }
    };

    const handleMarkAllRead = async () => {
        try {
            await markAllNotificationsAsRead();
            resetCount();
            setPreviewNotifications((prev) =>
                prev.map((n) => ({ ...n, read_at: new Date().toISOString() }))
            );
        } catch (e) {
            console.error("Error marking all notifications as read:", e);
        }
    };

    const handleNotificationClick = async (notif) => {
        setPopoverOpen(false);
        if (!notif.read_at) {
            try {
                await markNotificationAsRead(notif.id);
                decrementCount();
            } catch (e) {
                console.error("Error marking notification as read:", e);
            }
        }

        // Redirigir dinámicamente según la metadata recibida del backend
        if (notif.notifiable_type === "Follow") {
            navigate(`/users/${notif.actor?.id}`);
        } else if (notif.metadata?.report_slug) {
            const { country_code: cc, municipality_slug: mSlug, report_type: type, report_slug: rSlug } = notif.metadata;
            const typeStr = String(type || "").toLowerCase() === "lost" ? "lost" : "found";
            navigate(`/reports/${cc}/${mSlug}/${typeStr}/${rSlug}`);
        } else if (notif.metadata?.post_id) {
            navigate(`/posts/${notif.metadata.post_id}`);
        } else {
            navigate("/dashboard");
        }
    };

    return (
        <header
            className="
                hidden md:flex fixed inset-x-0 top-0 z-50
                bg-card-light dark:bg-card-dark
                border-b border-border-light dark:border-border-dark
              "
        >
            <div className="w-full max-w-6xl mx-auto px-4 lg:px-0 h-16 flex items-center justify-between gap-4">
                <div className="flex items-center">
                    {homeItem && (
                        <button
                            onClick={() => onSelect(homeItem.key)}
                            className={[
                                "inline-flex items-center justify-center rounded-full p-2.5 lg:p-3 transition-colors",
                                active === homeItem.key
                                    ? "bg-primary/10 dark:bg-primary/20 text-primary"
                                    : "text-foreground-light dark:text-foreground-dark hover:bg-background-light dark:hover:bg-background-dark",
                            ].join(" ")}
                            aria-current={active === homeItem.key ? "page" : undefined}
                            title={getLabel(homeItem)}
                        >
                            <svg
                                className="phosphor-icon"
                                viewBox="0 0 256 256"
                                fill="currentColor"
                                aria-hidden="true"
                            >
                                <path d={homeItem.iconPath} />
                            </svg>
                            <span className="sr-only">{getLabel(homeItem)}</span>
                        </button>
                    )}
                </div>

                <div className="flex items-center gap-0">
                    <nav className="flex items-center gap-1 lg:gap-1">
                        {navItems.map((item) => {
                            const isActive = active === item.key;
                            return (
                                <button
                                    key={item.key}
                                    onClick={() => onSelect(item.key)}
                                    className={[
                                        "inline-flex items-center justify-center rounded-full p-2.5 lg:p-3 transition-colors",
                                        isActive
                                            ? "bg-primary/10 dark:bg-primary/20 text-primary"
                                            : "text-foreground-light dark:text-foreground-dark hover:bg-background-light dark:hover:bg-background-dark",
                                    ].join(" ")}
                                    aria-current={isActive ? "page" : undefined}
                                    title={getLabel(item)}
                                >
                                    <svg
                                        className="phosphor-icon"
                                        viewBox="0 0 256 256"
                                        fill="currentColor"
                                        aria-hidden="true"
                                    >
                                        <path d={item.iconPath} />
                                    </svg>
                                    <span className="sr-only">{getLabel(item)}</span>
                                </button>
                            );
                        })}
                    </nav>

                    {/* Botón de Notificaciones y Popover */}
                    {isAuthenticated && (
                        <div className="relative" ref={popoverRef}>
                            <button
                                onClick={togglePopover}
                                className={[
                                    "inline-flex items-center justify-center rounded-full p-2.5 lg:p-3 transition-colors relative",
                                    popoverOpen
                                        ? "bg-primary/10 dark:bg-primary/20 text-primary"
                                        : "text-foreground-light dark:text-foreground-dark hover:bg-background-light dark:hover:bg-background-dark",
                                ].join(" ")}
                                title={t("common.notifications", { defaultValue: "Notifications" })}
                            >
                                <svg
                                    className="phosphor-icon"
                                    viewBox="0 0 256 256"
                                    fill="currentColor"
                                    aria-hidden="true"
                                >
                                    <path d="M221.8,175.94C216.25,166.38,208,139.33,208,104a80,80,0,0,0-160,0c0,35.33-8.26,62.38-13.81,71.94A16,16,0,0,0,48,200H88.33a40,40,0,0,0,79.34,0H208a16,16,0,0,0,13.8-24.06ZM128,216a24,24,0,0,1-22.62-16h45.24A24,24,0,0,1,128,216Zm80-32H48a.13.13,0,0,1,0-.07c5.8-10,14-38.31,14-79.93a64,64,0,0,1,128,0c0,41.62,8.2,70,14,79.93A.13.13,0,0,1,208,184Z" />
                                </svg>
                                {unreadCount > 0 && (
                                    <span className={`absolute top-1.5 right-1.5 flex h-4 items-center justify-center rounded-full bg-danger-light dark:bg-danger-dark text-[9px] font-bold text-white leading-none shadow-sm animate-heart-beat shrink-0 ${
                                        unreadCount < 10 ? "w-4 px-0" : "min-w-[16px] px-1"
                                    }`}>
                                        {unreadCount}
                                    </span>
                                )}
                            </button>

                            {/* Popover flotante estilo red social */}
                            {popoverOpen && (
                                <div className="absolute right-0 mt-2 w-80 max-h-[450px] overflow-hidden rounded-2xl border border-border-light dark:border-border-dark bg-card-light dark:bg-card-dark shadow-2xl z-[60] flex flex-col animate-chipMenuIn">
                                    <div className="p-3 border-b border-border-light dark:border-border-dark flex items-center justify-between">
                                        <span className="font-bold text-sm">{t("common.notifications", { defaultValue: "Notifications" })}</span>
                                    </div>
                                    <div className="flex-1 overflow-y-auto max-h-[300px] no-scrollbar">
                                        {loading ? (
                                            <div className="p-8 flex justify-center items-center">
                                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary"></div>
                                            </div>
                                        ) : previewNotifications.length === 0 ? (
                                            <div className="p-6 text-center text-xs text-subtle-light dark:text-subtle-dark">
                                                {t("notifications.empty", { defaultValue: "No notifications" })}
                                            </div>
                                        ) : (
                                            <div className="divide-y divide-border-light/50 dark:divide-border-dark/50">
                                                {previewNotifications.map((notif) => (
                                                    <div
                                                        key={notif.id}
                                                        onClick={() => handleNotificationClick(notif)}
                                                        className="p-3 text-xs flex gap-2.5 transition cursor-pointer hover:bg-neutral-surface dark:hover:bg-neutral-hover/30"
                                                    >
                                                        {/* Avatar del actor o círculo con letra inicial */}
                                                        <div className="flex-shrink-0">
                                                            {getAvatarUrlFromUser(notif.actor) ? (
                                                                <div
                                                                    className="w-8 h-8 rounded-full bg-cover bg-center border border-border-light dark:border-border-dark"
                                                                    style={{ backgroundImage: `url("${getAvatarUrlFromUser(notif.actor)}")` }}
                                                                />
                                                            ) : (
                                                                <div className="w-8 h-8 rounded-full bg-slate-200 dark:bg-zinc-700 flex items-center justify-center font-bold text-subtle-light dark:text-foreground-dark uppercase text-[10px]">
                                                                     {notif.actor?.name?.charAt(0) || "S"}
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <p className={`text-foreground-light dark:text-foreground-dark leading-tight ${!notif.read_at ? "font-bold" : "font-normal"}`}>
                                                                {notif.message}
                                                            </p>
                                                            <span className="text-[10px] text-caption-light dark:text-subtle-dark mt-1 block">
                                                                {getTimeAgo(notif.created_at, t, i18n)}
                                                            </span>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    <div className="p-2.5 border-t border-border-light dark:border-border-dark text-center bg-gray-50 dark:bg-black/20">
                                        {unreadCount > 0 ? (
                                            <button
                                                onClick={handleMarkAllRead}
                                                className="text-xs text-primary font-bold hover:underline w-full"
                                            >
                                                {t("notifications.mark_all_read", { defaultValue: "Mark all as read" })}
                                            </button>
                                        ) : (
                                            <span className="text-xs text-subtle-light dark:text-subtle-dark font-medium">
                                                {t("notifications.all_read", { defaultValue: "All notifications read" })}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Avatar + nombre */}
                    <button
                        type="button"
                        onClick={() => onSelect("profile")}
                        className="flex items-center rounded-full gap-2 mr-2"
                        aria-label={t('common.account_of', { name: displayName })}
                    >
                        <div
                            className="ml-2 bg-center bg-no-repeat bg-cover rounded-full size-7 lg:size-7 flex items-center justify-center text-xs font-semibold text-white uppercase"
                            style={
                                avatarUrl
                                    ? { backgroundImage: `url("${avatarUrl}")` }
                                    : { backgroundColor: "#64748b" }
                            }
                        >
                            {!avatarUrl && displayName.charAt(0)}
                        </div>
                    </button>
                </div>
            </div>
        </header>
    );
}